// Iter49 — Chain Map control-tower page.
// Full d3 directed-graph view with side detail, legend, filters, layout toggle.
// On small screens falls back to a list-by-layer view.
import React, { useEffect, useMemo, useState } from "react";
import { Network, Filter, Radio, Layers as LayersIcon, X, Building2, AlertTriangle, Mail, Phone, ArrowRight } from "lucide-react";
import { useT } from "../lib/i18n";
import { useStore } from "../store";
import { useTenantTypes } from "../api/sitroom";
import { ChainGraph, LAYER_COLORS, LAYER_LABELS, SEV_COLOR, EDGE_STATE, RISK_COLORS, RISK_LABELS } from "../components/chain/ChainGraph";

const API = process.env.REACT_APP_BACKEND_URL;
const tokenHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("sitroom_token") || ""}` });

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

// React-Query hook for the graph payload.
const useGraph = () => {
  const [data, setData] = useState({ nodes: [], edges: [], layer_counts: {}, active_incident_total: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/chain/graph`, { headers: tokenHeader() });
        if (!cancel && r.ok) setData(await r.json());
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);
  return { data, loading };
};

// Iter58 — SLA risk-forecast hook. Polls every 60s for live refresh.
const useSlaForecast = (enabled) => {
  const [data, setData] = useState({ tenants: [], all_tenants_risk: {}, cascade: {}, counts: { low: 0, elevated: 0, high: 0 } });
  useEffect(() => {
    if (!enabled) return;
    let cancel = false;
    const fetchOnce = async () => {
      const r = await fetch(`${API}/api/chain/sla-forecast`, { headers: tokenHeader() });
      if (!cancel && r.ok) setData(await r.json());
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 60_000);
    return () => { cancel = true; clearInterval(t); };
  }, [enabled]);
  const byTenant = useMemo(() => {
    const idx = {};
    (data.tenants || []).forEach((b) => { idx[b.tenant_id] = b; });
    return idx;
  }, [data]);
  return { ...data, byTenant };
};

const useTenantSummary = (tenantId) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!tenantId) { setData(null); return; }
    let cancel = false;
    (async () => {
      const r = await fetch(`${API}/api/chain/tenant/${encodeURIComponent(tenantId)}/summary`, { headers: tokenHeader() });
      if (!cancel && r.ok) setData(await r.json());
    })();
    return () => { cancel = true; };
  }, [tenantId]);
  return data;
};

export default function Chain() {
  const { t } = useT();
  const openPanel = useStore((s) => s.openPanel);
  const { data, loading } = useGraph();
  const { data: tenantTypes = [] } = useTenantTypes();
  const [layout, setLayout] = useState("force");
  const [filters, setFilters] = useState({ layer: "all", typeKey: "all", activeOnly: false, atRiskOnly: false });
  const [selectedId, setSelectedId] = useState(null);
  // Iter51 — Trace mode state (Phase 2). Pure UI.
  const [trace, setTrace] = useState({ nodeId: null, direction: "both" });
  // Iter58 — SLA risk forecast overlay state.
  const [showForecast, setShowForecast] = useState(false);
  const slaForecast = useSlaForecast(showForecast);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 640);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const summary = useTenantSummary(selectedId);
  const ttByKey = useMemo(() => Object.fromEntries(tenantTypes.map((x) => [x.key, x])), [tenantTypes]);

  // Filter chip data
  const layersAvailable = useMemo(
    () => [1, 2, 3, 4, 5].filter((l) => (data.layer_counts?.[String(l)] || data.layer_counts?.[l] || 0) > 0),
    [data]
  );

  return (
    <div className="px-4 sm:px-6 py-4 h-full flex flex-col" data-testid="chain-map-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 26, fontWeight: 600, color: "var(--text, #f0f2f8)", letterSpacing: "-0.3px" }}>
            {t("chain.title") || "Chain Map"}
          </h1>
          <div className="mt-1 uppercase" style={mono(10, "#f3b14a", 700, "1.4px")}>
            <Network size={11} className="inline-block mr-1" />
            Control-tower view · {data.nodes.length} tenants · {data.edges.length} relationships
            {data.active_incident_total > 0 && (
              <span style={{ color: "#E24B4A", marginLeft: 10 }}>
                <AlertTriangle size={11} className="inline mr-1" />{data.active_incident_total} active incidents
              </span>
            )}
          </div>
        </div>

        {/* Layout toggle */}
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { k: "force", label: "Force-directed" },
            { k: "layered", label: "Layered (L1 top → L5 bottom)" },
          ].map((opt) => (
            <button key={opt.k} type="button" onClick={() => setLayout(opt.k)}
              className="px-3 py-1.5 rounded inline-flex items-center gap-1"
              style={{
                background: layout === opt.k ? "rgba(24,95,165,0.20)" : "transparent",
                border: `1px solid ${layout === opt.k ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.10)"}`,
                color: layout === opt.k ? "#9ec3ec" : "var(--text-dim, #c5cad8)",
                ...mono(10, undefined, 700, "0.6px"), textTransform: "uppercase", cursor: "pointer",
              }}
              data-testid={`chain-layout-${opt.k}`}>
              <LayersIcon size={11} /> {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters + Legend bar */}
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <span style={mono(9, "#9198b0", 700, "0.8px")} className="uppercase">
          <Filter size={11} className="inline-block mr-1" />Filter
        </span>
        <select value={filters.layer} onChange={(e) => setFilters((f) => ({ ...f, layer: e.target.value }))}
          style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(10), padding: "5px 8px", borderRadius: 4 }}
          data-testid="chain-filter-layer">
          <option value="all">All layers</option>
          {layersAvailable.map((l) => <option key={l} value={l}>{LAYER_LABELS[l]}</option>)}
        </select>
        <select value={filters.typeKey} onChange={(e) => setFilters((f) => ({ ...f, typeKey: e.target.value }))}
          style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(10), padding: "5px 8px", borderRadius: 4 }}
          data-testid="chain-filter-type">
          <option value="all">All tenant types</option>
          {tenantTypes.map((tt) => <option key={tt.key} value={tt.key}>{tt.names?.en || tt.key}</option>)}
        </select>
        <label className="inline-flex items-center gap-1" style={mono(10, "var(--text-dim, #c5cad8)", 600, "0.4px")}>
          <input type="checkbox" checked={filters.activeOnly} onChange={(e) => setFilters((f) => ({ ...f, activeOnly: e.target.checked }))}
            data-testid="chain-filter-active-only" />
          Only tenants with active incidents
        </label>

        {/* Iter58 — SLA risk forecast toggle + at-risk-only filter. */}
        <label className="inline-flex items-center gap-1" style={mono(10, showForecast ? "#9aebc2" : "var(--text-dim, #c5cad8)", 600, "0.4px")} data-testid="chain-toggle-sla-forecast-label">
          <input type="checkbox" checked={showForecast} onChange={(e) => setShowForecast(e.target.checked)}
            data-testid="chain-toggle-sla-forecast" />
          Show SLA risk forecast
        </label>
        {showForecast && (
          <label className="inline-flex items-center gap-1" style={mono(10, "var(--text-dim, #c5cad8)", 600, "0.4px")} data-testid="chain-filter-at-risk-only-label">
            <input type="checkbox" checked={filters.atRiskOnly} onChange={(e) => setFilters((f) => ({ ...f, atRiskOnly: e.target.checked }))}
              data-testid="chain-filter-at-risk-only" />
            Only at-risk nodes
          </label>
        )}

        <span className="ml-auto flex items-center gap-3 flex-wrap" data-testid="chain-legend">
          {/* Iter51 — edge state legend (replaces the previous relationship-type-only legend) */}
          {Object.entries(EDGE_STATE).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1" data-testid={`chain-legend-edge-${k}`}>
              <svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke={v.stroke} strokeWidth={v.width + 0.5} /></svg>
              <span style={mono(9, "var(--text-dim, #c5cad8)", 600, "0.3px")}>{v.label}</span>
            </span>
          ))}
          <span className="hidden sm:inline" style={mono(8, "#3a3f50", 700)}>·</span>
          {[1, 2, 3, 4, 5].map((l) => (
            <span key={l} className="inline-flex items-center gap-1" style={{ opacity: layersAvailable.includes(l) ? 1 : 0.4 }}>
              <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: LAYER_COLORS[l] }} />
              <span style={mono(9, "var(--text-dim, #c5cad8)", 600, "0.3px")}>{LAYER_LABELS[l]}</span>
            </span>
          ))}
        </span>
      </div>

      {/* Iter58 — SLA risk forecast strip (counts + legend). Only when toggle is on. */}
      {showForecast && (
        <div className="mb-3 px-3 py-2 rounded flex flex-wrap items-center gap-3"
          style={{ background: "rgba(78,208,132,0.06)", border: "1px solid rgba(78,208,132,0.30)" }}
          data-testid="chain-sla-forecast-strip">
          <span style={mono(9, "#9aebc2", 700, "0.8px")} className="uppercase">
            <Radio size={11} className="inline mr-1" />Risk forecast · next 24h
          </span>
          {Object.entries(RISK_COLORS).map(([tier, color]) => (
            <span key={tier} className="inline-flex items-center gap-1" data-testid={`chain-sla-legend-${tier}`}>
              <span style={{
                display: "inline-block", width: 12, height: 12, borderRadius: "50%",
                border: `3px solid ${color}`, boxSizing: "border-box",
              }} />
              <span style={mono(10, "var(--text, #f0f2f8)", 700)}>
                {RISK_LABELS[tier]} <span style={{ color: "var(--text-dim, #9198b0)", fontWeight: 400 }}>· {slaForecast.counts?.[tier] || 0}</span>
              </span>
            </span>
          ))}
          {trace.nodeId && slaForecast.cascade?.[trace.nodeId] && (
            <span className="ml-auto" style={mono(10, "#ff9a99", 600)} data-testid="chain-sla-cascade-summary">
              <AlertTriangle size={11} className="inline mr-1" />
              If {data.nodes.find((n) => n.id === trace.nodeId)?.name || trace.nodeId} breaches →{" "}
              {slaForecast.cascade[trace.nodeId].downstream_at_risk?.length || 0} downstream at cascade risk
            </span>
          )}
        </div>
      )}

      {/* Iter51 — Trace mode toolbar */}
      {trace.nodeId && (
        <div className="mb-3 px-3 py-2 rounded flex flex-wrap items-center gap-2"
          style={{ background: "rgba(243,177,74,0.08)", border: "1px solid rgba(243,177,74,0.30)" }}
          data-testid="chain-trace-toolbar">
          <span style={mono(9, "#f3b14a", 700, "0.8px")} className="uppercase">Trace mode</span>
          <span style={mono(10, "var(--text, #f0f2f8)", 700)}>
            {data.nodes.find((n) => n.id === trace.nodeId)?.name || trace.nodeId}
          </span>
          <div className="flex items-center gap-1">
            {[
              { k: "upstream", label: "← Upstream" },
              { k: "downstream", label: "Downstream →" },
              { k: "both", label: "↔ Both" },
            ].map((opt) => (
              <button key={opt.k} type="button"
                onClick={() => setTrace((s) => ({ ...s, direction: opt.k }))}
                className="px-2 py-1 rounded"
                style={{
                  background: trace.direction === opt.k ? "rgba(243,177,74,0.20)" : "transparent",
                  border: `1px solid ${trace.direction === opt.k ? "rgba(243,177,74,0.55)" : "rgba(255,255,255,0.12)"}`,
                  color: trace.direction === opt.k ? "#f3b14a" : "var(--text-dim, #c5cad8)",
                  ...mono(9, undefined, 700, "0.4px"), textTransform: "uppercase", cursor: "pointer",
                }}
                data-testid={`chain-trace-dir-${opt.k}`}>
                {opt.label}
              </button>
            ))}
          </div>
          <button type="button"
            onClick={() => setTrace({ nodeId: null, direction: trace.direction })}
            className="ml-auto px-2 py-1 rounded"
            style={{
              background: "transparent",
              border: "1px solid rgba(226,75,74,0.40)",
              color: "#ff9a99",
              ...mono(9, undefined, 700, "0.4px"), textTransform: "uppercase", cursor: "pointer",
            }}
            data-testid="chain-trace-clear">
            Clear trace
          </button>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 grid gap-3" style={{ gridTemplateColumns: selectedId && !isMobile ? "minmax(0, 1fr) 320px" : "1fr", minHeight: 500 }}>
        <div
          className="rounded-md relative"
          style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.08)", overflow: "hidden", minHeight: 500 }}
          data-testid="chain-graph-wrap"
        >
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center" style={mono(11, "var(--dimmer, #9198b0)")}>
              Loading control-tower graph…
            </div>
          ) : isMobile ? (
            <MobileFallback data={data} onSelectNode={(n) => setSelectedId(n.id)} slaForecast={showForecast ? slaForecast : null} />
          ) : (
            <ChainGraph
              nodes={data.nodes}
              edges={data.edges}
              layout={layout}
              filters={filters}
              trace={trace}
              slaForecast={{ ...slaForecast, showForecast }}
              onSelectNode={(n) => {
                setSelectedId(n.id);
                // Iter51 — also set trace target on node click (with current direction)
                setTrace((s) => ({ ...s, nodeId: n.id }));
              }}
            />
          )}
        </div>

        {selectedId && (
          isMobile ? (
            <div
              className="fixed inset-0 z-[400] p-2 overflow-y-auto"
              style={{ background: "rgba(8,9,13,0.92)", backdropFilter: "blur(4px)" }}
              data-testid="chain-side-detail-mobile-overlay"
            >
              <SideDetail
                summary={summary}
                onClose={() => setSelectedId(null)}
                onOpenIncident={(id) => openPanel(id)}
                ttByKey={ttByKey}
              />
            </div>
          ) : (
            <SideDetail
              summary={summary}
              onClose={() => setSelectedId(null)}
              onOpenIncident={(id) => openPanel(id)}
              ttByKey={ttByKey}
            />
          )
        )}
      </div>
    </div>
  );
}

// Mobile fallback — list-by-layer with an 'Unassigned' bucket for layer-null tenants.
// Iter58 — when slaForecast is on, prepends an "At-risk tenants" section sorted
// by risk tier (high → elevated), each row showing tier + forecast breach count.
const MobileFallback = ({ data, onSelectNode, slaForecast }) => {
  const byLayer = useMemo(() => {
    const map = { 1: [], 2: [], 3: [], 4: [], 5: [], 0: [] };
    (data.nodes || []).forEach((n) => (map[n.layer || 0].push(n)));
    return map;
  }, [data]);
  const atRiskRows = useMemo(() => {
    if (!slaForecast?.byTenant) return [];
    return (data.nodes || [])
      .map((n) => ({ node: n, bucket: slaForecast.byTenant[n.id] }))
      .filter((r) => r.bucket && (r.bucket.risk_tier === "high" || r.bucket.risk_tier === "elevated"))
      .sort((a, b) => {
        const rank = { high: 0, elevated: 1 };
        return rank[a.bucket.risk_tier] - rank[b.bucket.risk_tier]
          || (b.bucket.forecast_breaches?.length || 0) - (a.bucket.forecast_breaches?.length || 0);
      });
  }, [data, slaForecast]);
  const renderGroup = (l, label, color) => (
    (byLayer[l]?.length || 0) > 0 && (
      <div key={l} className="mb-3">
        <div className="mb-1 px-2 py-1 rounded inline-block"
          style={{ background: `${color}26`, color, ...mono(9, undefined, 700, "0.6px"), textTransform: "uppercase" }}>
          {label} · {byLayer[l].length}
        </div>
        <div className="space-y-1">
          {byLayer[l].map((n) => (
            <button key={n.id} type="button" onClick={() => onSelectNode(n)}
              className="w-full text-left px-2 py-1.5 rounded inline-flex items-center gap-2"
              style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", cursor: "pointer", ...mono(11) }}
              data-testid={`chain-mobile-row-${n.id}`}>
              <span className="px-1.5 py-0.5 rounded" style={{ background: `${color}33`, color, ...mono(9, undefined, 700, "0.3px") }}>{n.initials}</span>
              <span style={{ fontWeight: 700 }}>{n.name}</span>
              {n.active_incidents > 0 && (
                <span style={{ color: SEV_COLOR[n.worst_severity] || "#E24B4A", marginLeft: "auto", ...mono(9, undefined, 700, "0.4px") }}>
                  <AlertTriangle size={9} className="inline mr-0.5" />{n.active_incidents}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    )
  );
  return (
    <div className="p-3 overflow-y-auto h-full" data-testid="chain-mobile-list">
      {/* Iter58 — At-risk tenants section (mobile only, when forecast is on). */}
      {slaForecast && atRiskRows.length > 0 && (
        <div className="mb-4" data-testid="chain-mobile-at-risk-section">
          <div className="mb-1 px-2 py-1 rounded inline-block"
            style={{ background: "rgba(226,75,74,0.15)", color: "#ff9a99", ...mono(9, undefined, 700, "0.6px"), textTransform: "uppercase" }}>
            At-risk tenants · {atRiskRows.length}
          </div>
          <div className="space-y-1">
            {atRiskRows.map(({ node, bucket }) => {
              const color = RISK_COLORS[bucket.risk_tier];
              return (
                <button key={node.id} type="button" onClick={() => onSelectNode(node)}
                  className="w-full text-left px-2 py-1.5 rounded inline-flex items-center gap-2"
                  style={{ background: "var(--bg-surface, #0d0f18)", border: `1px solid ${color}55`, color: "var(--text, #f0f2f8)", cursor: "pointer", ...mono(11) }}
                  data-testid={`chain-mobile-at-risk-${node.id}`}>
                  <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", border: `3px solid ${color}`, boxSizing: "border-box" }} />
                  <span style={{ fontWeight: 700 }}>{node.name}</span>
                  <span className="ml-auto" style={mono(9, color, 700, "0.4px")}>
                    {RISK_LABELS[bucket.risk_tier]} · {bucket.forecast_breaches.length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {[1, 2, 3, 4, 5].map((l) => renderGroup(l, LAYER_LABELS[l], LAYER_COLORS[l]))}
      {renderGroup(0, "Unassigned", LAYER_COLORS[0])}
    </div>
  );
};

const SideDetail = ({ summary, onClose, onOpenIncident, ttByKey }) => {
  if (!summary) {
    return (
      <div className="rounded-md p-4" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}>
        <div style={mono(11, "var(--dimmer, #9198b0)")}>Loading tenant…</div>
      </div>
    );
  }
  const { tenant, incidents = [], relationships_in = [], relationships_out = [] } = summary;
  if (!tenant) {
    return (
      <div className="rounded-md p-4" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}>
        <div style={mono(11, "var(--dimmer, #9198b0)")}>Tenant not found.</div>
      </div>
    );
  }
  const tt = tenant.tenant_type_key ? ttByKey[tenant.tenant_type_key] : null;
  return (
    <aside className="rounded-md overflow-y-auto" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)", maxHeight: "calc(100vh - 220px)" }} data-testid="chain-side-detail">
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div>
          <div className="flex items-center gap-2">
            <Building2 size={14} color="#9ec3ec" />
            <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>{tenant.name}</span>
          </div>
          <div className="mt-0.5" style={mono(9, "#9198b0", 700, "0.6px")}>
            {tt ? `${tt.names?.en} · L${tt.layer}` : tenant.type || "(no type)"}
          </div>
        </div>
        <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", color: "#9198b0", cursor: "pointer" }} data-testid="chain-side-close"><X size={14} /></button>
      </div>

      {/* Active incidents */}
      <section className="px-4 py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
        <div className="mb-1.5 uppercase" style={mono(9, "#9198b0", 700, "0.6px")}>
          <Radio size={9} className="inline mr-1" />Active incidents · {incidents.length}
        </div>
        {incidents.length === 0 && <div style={mono(10, "var(--dimmer, #9198b0)")}>None.</div>}
        <div className="space-y-1">
          {incidents.map((i) => (
            <button key={i.id} type="button" onClick={() => onOpenIncident(i.id)}
              className="w-full text-left px-2 py-1 rounded inline-flex items-center gap-2"
              style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.06)", color: "var(--text, #f0f2f8)", cursor: "pointer", ...mono(10) }}
              data-testid={`chain-side-incident-${i.id}`}>
              <span className="px-1 py-0.5 rounded" style={{ background: `${SEV_COLOR[i.severity?.toLowerCase()] || "#9198b0"}33`, color: SEV_COLOR[i.severity?.toLowerCase()] || "#9198b0", ...mono(9, undefined, 700) }}>{(i.severity || "").toUpperCase()}</span>
              <span style={{ fontFamily: "DM Mono, monospace", color: "#9ec3ec", fontSize: 10 }}>{i.id}</span>
              <span className="truncate" style={{ flex: 1 }}>{i.title || ""}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Contacts */}
      <section className="px-4 py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
        <div className="mb-1.5 uppercase" style={mono(9, "#9198b0", 700, "0.6px")}>Contacts · {(tenant.contacts || []).length}</div>
        <div className="space-y-1">
          {(tenant.contacts || []).map((c) => (
            <div key={c.id} style={mono(10, "var(--text, #f0f2f8)")} data-testid={`chain-side-contact-${c.id}`}>
              <b>{c.name}</b>{c.role ? <span style={{ color: "var(--text-dim, #c5cad8)" }}> · {c.role}</span> : null}
              {c.email && <div style={mono(9, "#9ec3ec")}><Mail size={9} className="inline mr-0.5" />{c.email}</div>}
              {c.phone && <div style={mono(9, "#9ec3ec")}><Phone size={9} className="inline mr-0.5" />{c.phone}</div>}
            </div>
          ))}
          {(tenant.contacts || []).length === 0 && <div style={mono(10, "var(--dimmer, #9198b0)")}>No contacts.</div>}
        </div>
      </section>

      {/* Relationships out */}
      <section className="px-4 py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
        <div className="mb-1.5 uppercase" style={mono(9, "#9198b0", 700, "0.6px")}>Outgoing · {relationships_out.length}</div>
        <div className="space-y-1">
          {relationships_out.map((r) => (
            <div key={r.id} style={mono(10, "var(--text, #f0f2f8)")}>
              <ArrowRight size={9} className="inline mr-0.5" color="#9ec3ec" /> {r.relationship_type} → <b>{r.to_tenant_id}</b>
              {r.part_numbers?.length > 0 && <div style={mono(9, "#f3b14a")}>{r.part_numbers.join(", ")}</div>}
            </div>
          ))}
          {relationships_out.length === 0 && <div style={mono(10, "var(--dimmer, #9198b0)")}>None.</div>}
        </div>
      </section>

      {/* Relationships in */}
      <section className="px-4 py-2.5">
        <div className="mb-1.5 uppercase" style={mono(9, "#9198b0", 700, "0.6px")}>Incoming · {relationships_in.length}</div>
        <div className="space-y-1">
          {relationships_in.map((r) => (
            <div key={r.id} style={mono(10, "var(--text, #f0f2f8)")}>
              <ArrowRight size={9} className="inline mr-0.5" color="#f3b14a" style={{ transform: "rotate(180deg)" }} /> <b>{r.from_tenant_id}</b> {r.relationship_type}
              {r.part_numbers?.length > 0 && <div style={mono(9, "#f3b14a")}>{r.part_numbers.join(", ")}</div>}
            </div>
          ))}
          {relationships_in.length === 0 && <div style={mono(10, "var(--dimmer, #9198b0)")}>None.</div>}
        </div>
      </section>
    </aside>
  );
};
